/**
 * RouteDetailDrawer — right-side slide-over that shows a route's map + stops
 * when a Routes list row/card is clicked.
 *
 * Wiring (in RoutesListView), mirrors VoterDetailDrawer:
 *   const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
 *   <DataTable onRowClick={(row) => setSelectedRouteId(row.id)} ... />
 *   <RouteDetailDrawer
 *     routeId={selectedRouteId}
 *     onClose={() => setSelectedRouteId(null)}
 *   />
 *
 * Passing `routeId === null` keeps the drawer closed. The moment it becomes a
 * number, the drawer fetches `/routes/{routeId}` (which returns the route with
 * ordered stops + geometry) and slides in. The last-loaded route is kept in
 * state during the close transition so the content doesn't blank out mid-slide.
 *
 * The heavy lifting — map, stop-by-stop viewer, CSV export — is delegated to
 * the existing RouteViewer, which takes a fully-loaded Route (with `.stops`).
 */
import React, { useEffect, useRef, useState } from 'react';
import client from '@/api/client';
import type { Route } from '@/types';
import RouteViewer from '@/components/RouteViewer';

interface RouteDetailDrawerProps {
  routeId: number | null; // null = drawer closed
  onClose: () => void;
}

const RouteDetailDrawer: React.FC<RouteDetailDrawerProps> = ({
  routeId,
  onClose,
}) => {
  const isOpen = routeId !== null;
  const [route, setRoute] = useState<Route | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{ message: string; status?: number } | null>(
    null,
  );
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  // The drawer panel we promote to native full screen so the whole popup
  // (map + stats + stop list) becomes the main view, not just the map pane.
  const asideRef = useRef<HTMLElement | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);

  const toggleMaximize = () => {
    const el = asideRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  };

  // Sync with the browser (covers Esc-to-exit). The InteractiveMap's own
  // FullscreenControl already listens for fullscreenchange and calls
  // invalidateSize(), so the map inside reflows to the new size for free.
  useEffect(() => {
    const onChange = () =>
      setIsMaximized(document.fullscreenElement === asideRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Fetch whenever routeId changes (null → number, or number → different number).
  const fetchRoute = React.useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get<Route>(`/routes/${id}`);
      setRoute(res.data);
    } catch (e: any) {
      const status: number | undefined = e?.response?.status;
      const detail: string | undefined =
        e?.response?.data?.detail || e?.message;
      setError({ message: detail || 'Failed to load route.', status });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (routeId === null) return;
    fetchRoute(routeId);
  }, [routeId, fetchRoute]);

  // Escape key closes.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      // While maximized, let Esc exit full screen (browser default) rather
      // than closing the whole drawer.
      if (e.key === 'Escape' && !document.fullscreenElement) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Focus the close button on open.
  useEffect(() => {
    if (isOpen && closeBtnRef.current) {
      const t = window.setTimeout(() => {
        closeBtnRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!isOpen}
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-200 ${
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Drawer panel — wider than the voter drawer because RouteViewer needs
          room for its map + stops columns. */}
      <aside
        ref={asideRef}
        role="dialog"
        aria-modal="true"
        aria-label="Route details"
        aria-hidden={!isOpen}
        {...(!isOpen && { inert: '' })}
        className={`fixed inset-y-0 right-0 w-full bg-bg-base border-l border-border-base shadow-2xl z-50 transform transition-transform duration-200 ease-out flex flex-col ${
          isMaximized ? 'max-w-none' : 'max-w-5xl'
        } ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="p-4 border-b border-border-base bg-bg-raised/60 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-fg-primary truncate">
              {route?.name || (loading ? 'Loading route…' : 'Route')}
            </h2>
            {route && (
              <div className="text-xs text-fg-muted mt-0.5 truncate">
                {[
                  route.subregion,
                  route.precinct_num ? `Precinct ${route.precinct_num}` : null,
                  `${route.door_count} doors`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
          <div className="flex-shrink-0 flex items-center gap-1">
            {/* Expand the whole popup (map + stats + stops) to full screen. */}
            <button
              onClick={toggleMaximize}
              aria-label={isMaximized ? 'Exit full screen' : 'View full screen'}
              title={isMaximized ? 'Exit full screen' : 'View full screen'}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-status-success"
            >
              {isMaximized ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                  <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                  <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                  <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                  <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                  <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                  <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
              )}
            </button>
            <button
              ref={closeBtnRef}
              onClick={onClose}
              aria-label="Close route details"
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-status-success"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && !route && (
            <div className="flex items-center justify-center h-full text-fg-muted">
              <p>Loading route…</p>
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-status-danger-bg/70 bg-status-danger-bg/20 p-4">
              <div className="text-status-danger font-semibold">
                {error.status === 403
                  ? 'Access restricted'
                  : 'Could not load route'}
              </div>
              <p className="text-sm text-status-danger/90 mt-2">
                {error.status === 403
                  ? "Access restricted — your role can't view this route."
                  : error.message}
              </p>
              {error.status !== 403 && routeId !== null && (
                <button
                  onClick={() => fetchRoute(routeId)}
                  className="mt-3 inline-flex items-center px-3 py-1.5 rounded bg-accent hover:bg-accent-hover text-white text-sm font-semibold focus:outline-none"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {route && !error && (route.stops?.length ? (
            <div className="h-full">
              <RouteViewer route={route} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-fg-muted gap-1">
              <p className="text-fg-secondary font-medium">This route has no stops yet</p>
              <p className="text-sm">
                Add stops from the optimizer to see it on the map.
              </p>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default RouteDetailDrawer;
